import React, { useState } from 'react';
import axios from 'axios';
import { Star, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ReviewPopup({ planType, onClose }) {
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, {
        rating,
        comment: comment.trim() || null,
        plan_type: planType,
      }, { withCredentials: true });
      setSubmitted(true);
      setTimeout(() => onClose(), 2500);
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="review-popup-success">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-sm mx-4 bg-[#0c0c12] border border-white/10 rounded-2xl p-8 text-center animate-in fade-in-0 zoom-in-95 duration-300">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <Star className="w-7 h-7 text-emerald-400 fill-emerald-400" />
          </div>
          <h3 className="font-clash text-xl font-semibold text-white mb-2">Thanks for your feedback!</h3>
          <p className="text-white/50 text-sm">Your review helps other creatives get paid faster.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="review-popup">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm mx-4 bg-[#0c0c12] border border-white/10 rounded-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors z-10"
          data-testid="review-popup-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-7 pt-7 pb-5 text-center">
          <h3 className="font-clash text-xl font-semibold text-white mb-1" data-testid="review-popup-title">
            How was your experience?
          </h3>
          <p className="text-white/45 text-sm mb-6">
            Your feedback helps other creatives get paid faster
          </p>

          {/* Stars */}
          <div className="flex justify-center gap-1.5 mb-6" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                onClick={() => setRating(star)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`review-star-${star}`}
              >
                <Star
                  className={`w-8 h-8 transition-colors ${
                    star <= (hoveredStar || rating)
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-white/15'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Comment */}
          <Textarea
            placeholder="Tell us what you liked (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25 min-h-[80px] resize-none focus:border-[#FF4500]/40 text-sm mb-4"
            data-testid="review-comment-input"
          />
        </div>

        {/* Actions */}
        <div className="px-7 pb-7 flex flex-col gap-2">
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-[#FF4500] hover:bg-[#CC3700] text-white h-10 rounded-xl font-medium disabled:opacity-40"
            data-testid="review-submit-btn"
          >
            {submitting ? 'Submitting...' : 'Leave Review'}
          </Button>
          <button
            onClick={onClose}
            className="text-white/30 hover:text-white/50 text-sm py-1.5 transition-colors"
            data-testid="review-skip-btn"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
